import groupBy from 'lodash/groupBy';

import { fetchQuery, graphql } from 'react-relay';
// eslint-disable-next-line jira/import-whitelist
import { addSpanToAll } from '@atlaskit/react-ufo/interaction-metrics';
// eslint-disable-next-line jira/import-whitelist
import getRelayEnvironment from '@atlassian/jira-relay-environment/src/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { getPriorityGroupDataByProjectIdQuery } from '@atlassian/jira-relay/src/__generated__/getPriorityGroupDataByProjectIdQuery.graphql';
import type { CategorizedGroupData, PriorityGroup, QueueId } from '../../types.tsx';

type GroupResponse = PriorityGroup & {
	queueIds: QueueId[];
	category: string;
};

type GroupFetchResponse = {
	results: GroupResponse[];
};

const fetchGroupsByKey = async (projectKey: string, cloudId: string) => {
	return fetchJson(
		`/gateway/api/jsm/queues/cloudid/${cloudId}/api/v1/groups?projectIdOrKey=${projectKey}${
			fg('add-operation-query-param-to-queues-endpoints') ? '&operation=fetchPriorityGroups' : ''
		}`,
	);
};

const fetchGroupsById = async (projectId: number, cloudId: string) => {
	return fetchJson(
		`/gateway/api/jsm/queues/cloudid/${cloudId}/api/v1/groups?projectId=${projectId}${
			fg('add-operation-query-param-to-queues-endpoints')
				? '&operation=fetchPriorityGroupsByProjectId'
				: ''
		}`,
	);
};

const fetchGroupsByIdGraphql = async (projectId: number, cloudId: string) => {
	const projectAri = `ari:cloud:jira:${cloudId}:project/${projectId}`;
	const result = await fetchQuery<getPriorityGroupDataByProjectIdQuery>(
		getRelayEnvironment(),
		graphql`
			query getPriorityGroupDataByProjectIdQuery($projectAri: ID!) {
				queuesprioritygroups_priorityGroups(projectId: $projectAri) {
					results {
						id
						name
						category
						queueIds
						version
						creationMethod
					}
				}
			}
		`,
		{ projectAri },
	).toPromise();

	return {
		results:
			result?.queuesprioritygroups_priorityGroups?.results?.reduce(
				(priorityGroups, priorityGroup) => {
					// this mapping function is to make types happy
					if (
						priorityGroup &&
						priorityGroup.id &&
						priorityGroup.name &&
						priorityGroup.category &&
						priorityGroup.queueIds
					) {
						priorityGroups.push({
							id: priorityGroup.id,
							name: priorityGroup.name,
							category: priorityGroup.category,
							queueIds: priorityGroup.queueIds.reduce((queueIds, queueId) => {
								if (queueId !== null && queueId !== undefined) {
									queueIds.push(queueId);
								}
								return queueIds;
								// we need the below, otherwise reduce things the type is never[]
								// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							}, [] as QueueId[]),
							...(priorityGroup.version ? { version: priorityGroup.version } : {}),
							...(priorityGroup.creationMethod
								? { creationMethod: priorityGroup.creationMethod }
								: {}),
						});
					}
					return priorityGroups;
				},
				// we need the below, otherwise reduce things the type is never[]
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				[] as GroupResponse[],
			) ?? [],
	};
};

export const getPriorityGroupDataByProjectId = async (
	projectId: number | undefined,
	projectKey: string,
	cloudId: string,
) => {
	const fetchStartTime = performance.now();
	let data: GroupFetchResponse;
	if (projectId) {
		fg('queues-prioritygroups-relay')
			? (data = await fetchGroupsByIdGraphql(projectId, cloudId))
			: (data = await fetchGroupsById(projectId, cloudId));

		if (!fg('add-operation-query-param-to-queues-endpoints')) {
			addSpanToAll(
				'fetch',
				'fetchPriorityGroupsByProjectId',
				[{ name: 'network' }],
				fetchStartTime,
				performance.now(),
			);
		}
	} else {
		fireErrorAnalytics({
			meta: {
				id: 'fetchPriorityGroupsProjectIdUndefined',
				packageName: 'jiraRouterResourcesServiceDeskQueues',
				teamName: 'jsm-flash',
			},
			sendToPrivacyUnsafeSplunk: true,
		});
		data = await fetchGroupsByKey(projectKey, cloudId);

		if (!fg('add-operation-query-param-to-queues-endpoints')) {
			addSpanToAll(
				'fetch',
				'fetchPriorityGroups',
				[{ name: 'network' }],
				fetchStartTime,
				performance.now(),
			);
		}
	}

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const processedData = groupBy(
		data.results.map((group) => ({ ...group, queues: group.queueIds || [] })),
		'category',
	) as unknown;

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return processedData as CategorizedGroupData;
};
